import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { graphql, useStaticQuery } from 'gatsby';
import { classNames, Icon, List } from '@pella/sash-framework';
import { Image } from '../../..';
import { isValidArray } from '../../../utilities/validations';

const HoverBannerRTA = ({ className, compact }) => {
	const { hover_cards } = useStaticQuery(query).contentstackHoverConfig;
	const hover = hover_cards.find((hover) => hover.placement.includes('rta'));
	const videoRef = useRef(null);
	const [isPlaying, setIsPlaying] = useState(hover?.file?.url ? true : false);

	if (!hover) return null;

	const classes = classNames({
		'hover-banner bg-[#685f5f]/50 grid px-6 pb-8 pt-6 md:py-8 md:px-14 md:grid-cols-[1fr_auto] max-w-[762px] md:gap-x-8 items-center': true,
		'xl:px-6 xl:pt-6 xl:pb-0 xl:grid-cols-1 max-w-[380px] mt-4 !items-start mb-10': compact,
		'xl:py-10 xl:gap-x-10 mb-10': !compact,
		[className]: className,
	});

	const imageWrapperClasses = classNames({
		'hover-banner-image-wrapper relative col-start-1 md:col-start-2 row-start-1 md:row-span-2 w-[188px] md:w-[252px] mx-auto mb-6 md:mb-0': true,
		'xl:row-span-1 xl:col-start-1 xl:row-start-3 xl:mt-3 self-end xl:row-span-1 md:h-[188px] xl:w-[146px] xl:h-auto': compact,
		'xl:row-span-4': !compact,
		'p-5 md:p-6 bg-black/40': isValidArray(hover.image),
		'xl:p-4': isValidArray(hover.image) && compact,
		'xl:p-8': isValidArray(hover.image) && !compact,
	});

	const imageClasses = classNames({
		'hover-banner-image mx-auto max-w-full rounded-[0.625rem] aspect-video': true,
		'xl:aspect-[1.34] xl:rounded-b-none': compact,
	});

	const headingClasses = classNames({
		'text-subtitle hover-banner-heading col-start-1 row-start-2 text-center max-md:mx-auto max-w-[18ch] md:row-start-1 md:text-left mb-5 md:mb-6': true,
		'md:text-h3 xl:text-subtitle xl:mb-4': compact,
		'md:text-h3 xl:row-start-2': !compact,
	});

	const listClasses = classNames({
		'hover-banner-badge col-start-1 row-start-3 space-y-3 md:row-start-2 md:space-y-4': true,
		'xl:row-start-3': !compact,
	});

	const bulletHeadingClasses = classNames({
		'text-black xl:text-base/tight': true,
		'!text-body-2': compact,
	});

	const onVideControlClick = (event) => {
		event.preventDefault();

		if (!videoRef.current.paused) {
			videoRef.current.pause();
			setIsPlaying(false);
		} else {
			videoRef.current.play();
			setIsPlaying(true);
		}
	};

	return (
		<div
			className={classes}
			data-testid='hover-banner-rta'
		>
			<div className={imageWrapperClasses}>
				{isValidArray(hover.image) && (
					<Image
						data={hover.image[0]}
						className={imageClasses}
					/>
				)}

				{hover.file?.url && (
					<>
						<video
							data-testid='hover-banner-video'
							className='hover-banner-video aspect-[1.43] w-full object-cover'
							autoPlay
							muted
							loop
							playsInline
							ref={videoRef}
						>
							<source
								src={hover.file.url}
								type='video/mp4'
							/>
							Your browser does not support the video tag.
						</video>

						<button
							onClick={onVideControlClick}
							className='absolute bottom-1.5 right-1.5 flex h-6 w-6 items-center justify-center bg-black/80'
							aria-label={isPlaying ? 'Pause video' : 'Play video'}
						>
							<Icon
								name={isPlaying ? 'pause' : 'play_arrow'}
								className='text-white'
								fill={1}
								size={18}
							/>
						</button>
					</>
				)}
			</div>

			{hover.heading && <h3 className={headingClasses}>{hover.heading}</h3>}

			{isValidArray(hover.bullets) && (
				<List
					bullets='none'
					className={listClasses}
				>
					{hover.bullets.map((bullet, index) => (
						<li
							key={index}
							className='flex items-center gap-x-4 xl:!mt-2.5 xl:first:!mt-0'
						>
							{bullet.material_symbol && <Icon name={bullet.material_symbol} />}
							{bullet.file?.url && (
								<Image
									data={{ image: bullet.file }}
									className='w-4 flex-none'
								/>
							)}
							<span className={bulletHeadingClasses}>{bullet.heading}</span>
						</li>
					))}
				</List>
			)}
		</div>
	);
};

HoverBannerRTA.propTypes = {
	template: PropTypes.string.isRequired,
	className: PropTypes.string,
	compact: PropTypes.bool,
};

export default HoverBannerRTA;

export const query = graphql`
	{
		contentstackHoverConfig {
			hover_cards {
				placement
				image {
					...Image
					image {
						url
						gatsbyImageData(quality: 90)
					}
				}
				file {
					url
				}
				heading
				bullets {
					material_symbol
					file {
						url
						gatsbyImageData(quality: 90)
					}
					heading
				}
			}
		}
	}
`;
